import {v4 as uuidv4} from 'uuid';
import SafaricomService from "@/services/safaricomService";
import DcbService from "@/services/dcbService";
import store from "@/store";
import {showBrandDebugAlert} from "@/functions/brandDebug";

// Base URL for the Safaricom DSDP consent gateway. The full redirect target is
// `${CONSENT_GATEWAY_BASE_URL}/${offerCode}` with the transaction id appended as a query param.
const CONSENT_GATEWAY_BASE_URL = 'https://dsdp-cg.safaricom.com/consent-gateway';

// TODO(DSDP): confirm the exact query-param name the consent gateway expects for the transaction id.
// Defaulting to `transaction_id` to mirror the inbound landing-page param. Change here if the spec differs.
const CONSENT_GATEWAY_TRANSACTION_ID_PARAM = 'transaction_id';

function buildConsentGatewayUrl(offerCode, transactionId) {
    const url = new URL(`${CONSENT_GATEWAY_BASE_URL}/${offerCode}`);
    if (transactionId) url.searchParams.set(CONSENT_GATEWAY_TRANSACTION_ID_PARAM, transactionId);
    return url.toString();
}

const redirectToErrorPage = (router, query = {}) => {
    if (!router) return;
    router.push({name: 'Error', query});
};

export function isMFilterCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('mfilter_id') && urlParams.has('requestid');
}

export function handleMFilterCallback(router, brandConfig) {
    const urlParams = new URLSearchParams(window.location.search);
    const requestId = urlParams.get('requestid');
    const mfilterId = urlParams.get('mfilter_id');
    const callbackMsisdn = urlParams.get('msisdn');
    const message = urlParams.get('message');
    const error = urlParams.get('error');

    console.log('MFilter callback URL params:', {
        requestId,
        mfilterId,
        callbackMsisdn,
        message,
        error,
    });

    const storedRequestId = localStorage.getItem('mfilter_request_id');
    const storedMfId = localStorage.getItem('mfilter_mf_id');
    const storedMsisdn = localStorage.getItem('mfilter_msisdn');


    showBrandDebugAlert(
        brandConfig,
        'Callback Received\n\n' +
        '--- URL Params ---\n' +
        'requestId: ' + requestId + '\n' +
        'mfilter_id: ' + mfilterId + '\n' +
        'msisdn: ' + callbackMsisdn + '\n' +
        'message: ' + message + '\n' +
        'error: ' + error + '\n\n' +
        '--- Stored Values ---\n' +
        'storedRequestId: ' + storedRequestId + '\n' +
        'storedMfId: ' + storedMfId + '\n' +
        'storedMsisdn: ' + storedMsisdn + '\n\n' +
        '--- Validation ---\n' +
        'msisdnMatch: ' + (callbackMsisdn === storedMsisdn) + '\n' +
        'requestIdMatch: ' + (requestId === storedRequestId) + '\n' +
        'mfilterIdMatch: ' + (mfilterId === storedMfId)
    );

    // requestId is the anti-tamper token and is always stored, so it is the primary check.
    // mfilter_id is no longer issued (we redirect straight to the DSDP consent gateway and never
    // call get-cg), so storedMfId is always null and is intentionally excluded from validation.
    // msisdn is validated only when we captured one client-side; when the gateway did the
    // identification we have no storedMsisdn to compare against, so we do not fail on it.
    const requestIdValid = requestId === storedRequestId;
    const msisdnValid = !storedMsisdn || callbackMsisdn === storedMsisdn;
    if (!requestIdValid || !msisdnValid) {
        console.warn('MFilter validation failed:', {
            msisdnMatch: msisdnValid,
            requestIdMatch: requestIdValid,
        });
        showBrandDebugAlert(brandConfig, 'Validation FAILED - redirecting to error page');
        localStorage.clear();
        redirectToErrorPage(router, {reason: 'mfilter_validation_failed'});
        return;
    }

    if (message === 'Success') {
        console.log('MFilter success, redirecting to ThankYou with msisdn:', callbackMsisdn);
        showBrandDebugAlert(brandConfig, 'Callback SUCCESS - redirecting to ThankYou with msisdn: ' + callbackMsisdn);
        localStorage.clear();
        router.push({name: 'ThankYou', query: {msisdn: callbackMsisdn}});
    } else {
        console.error('MFilter error:', {error, message});
        showBrandDebugAlert(brandConfig, 'Callback FAILED\nerror: ' + error + '\nmessage: ' + message);
        localStorage.clear();
        redirectToErrorPage(router, {reason: 'mfilter_error', error, message});
    }
}

export async function handleMFilterFlow(brandConfig, router, options = {}) {
    // Attribution (clickId/campaignId/publisherId) is carried inside the transaction_id we forward to
    // the consent gateway and is recovered server-side from the subscription callback, so we no longer
    // queue the subscription here. The cross-sell flow has no inbound transaction_id, so it composes one
    // (`<clickId>-<campaignId>`) and passes it in via options.transactionId.
    const {msisdn: providedMsisdn, transactionId: providedTransactionId} = options;
    const userAgent = navigator.userAgent;
    const sourceIP = await SafaricomService.getSourceIP();
    console.log('MFilter flow started:', {userAgent, sourceIP});
    const urlParams = new URLSearchParams(window.location.search);

    let requestId = localStorage.getItem('mfilter_request_id');
    if (providedTransactionId) {
        requestId = providedTransactionId;
        localStorage.setItem('mfilter_request_id', requestId);
        console.log('MFilter using provided transactionId:', requestId);
    } else if (urlParams.get('transaction_id') || !requestId) {
        requestId = urlParams.get('transaction_id') ?? uuidv4();
        localStorage.setItem('mfilter_request_id', requestId);
        console.log('MFilter generated new requestId:', requestId);
    } else {
        console.log('MFilter using existing requestId:', requestId);
    }

    const eMsisdn = urlParams.get('e-msisdn');
    console.log('MFilter eMsisdn from URL:', eMsisdn);

    let decryptedMsisdn;
    if (eMsisdn) {
        const response = await DcbService.decryptUserMsisdn(eMsisdn);
        console.log('MFilter decryptUserMsisdn response:', response);
        if (response.rawMsisdn) decryptedMsisdn = response.rawMsisdn;
        console.log('MFilter decryptedMsisdn:', decryptedMsisdn);
    }
    let msisdnValue = providedMsisdn ?? store?.state?.msisdn;
    if (!msisdnValue) {
        const response = await SafaricomService.getMSISDN(decryptedMsisdn);
        console.log('MFilter getMSISDN response:', response);
        msisdnValue = response?.msisdn;
        if (response?.msisdn) {
            store.commit('setMsisdn', response.msisdn);
            localStorage.setItem('mfilter_msisdn', response.msisdn);
            console.log('MFilter msisdn set:', response.msisdn);
        }
    } else {
        localStorage.setItem('mfilter_msisdn', msisdnValue);
    }


    showBrandDebugAlert(
        brandConfig,
        'Flow Started - User Details\n\n' +
        'userAgent: ' + userAgent + '\n' +
        'sourceIP: ' + sourceIP + '\n' +
        'requestId: ' + requestId + '\n' +
        'eMsisdn (from URL): ' + eMsisdn + '\n' +
        'decryptedMsisdn: ' + decryptedMsisdn + '\n' +
        'msisdn: ' + msisdnValue + '\n' +
        'brand: ' + brandConfig.brand + '\n' +
        'offerCode: ' + brandConfig.offerCode + '\n' +
        'mFilterOfferCode (legacy): ' + brandConfig.mFilterOfferCode
    );

    // The DSDP consent gateway performs its own on-network MSISDN detection, so a
    // client-side msisdn is NOT required to proceed — it is only used (when available)
    // for callback validation. Do NOT hard-fail here: users whose client-side lookup
    // misses (e.g. desktop/WiFi/flaky enrichment) can still be identified and billed by
    // the gateway. A hard error here was blocking those users from subscribing.
    if (!msisdnValue) {
        console.warn('MFilter msisdn unavailable client-side; proceeding to consent gateway for server-side detection');
        showBrandDebugAlert(brandConfig, 'MSISDN unavailable client-side - proceeding to consent gateway anyway');
    }

    try {
        if (!brandConfig.offerCode) {
            console.warn('MFilter offerCode unavailable, cannot build consent gateway URL');
            showBrandDebugAlert(brandConfig, 'offerCode unavailable - redirecting to error page');
            redirectToErrorPage(router, {reason: 'mfilter_offercode_unavailable'});
            return;
        }

        const consentGatewayUrl = buildConsentGatewayUrl(brandConfig.offerCode, requestId);
        console.log('MFilter redirecting to consent gateway:', consentGatewayUrl);

        showBrandDebugAlert(
            brandConfig,
            'Before Redirect\n\n' +
            'offerCode: ' + brandConfig.offerCode + '\n' +
            'transactionId: ' + requestId + '\n' +
            'consentGatewayUrl: ' + consentGatewayUrl
        );

        window.location.href = consentGatewayUrl;
    } catch (error) {
        console.error('MFilter consent gateway redirect error:', error);
        showBrandDebugAlert(brandConfig, 'Consent gateway redirect ERROR:\n' + error.message + '\n\nStack: ' + error.stack);
        redirectToErrorPage(router, {reason: 'mfilter_request_failed'});
    }
}
